import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch(`${API}/auth/me`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
        return userData;
      } else if (response.status === 401 || response.status === 403) {
        // Only clear auth state on a definitive "not authenticated" response
        setUser(null);
        setIsAuthenticated(false);
        return null;
      }
      // Transient errors (429, 5xx): keep the current auth state
      return null;
    } catch (error) {
      // Network errors: keep the current auth state
      console.error('Auth check failed:', error);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Check authentication on mount (skip when returning from Google login —
  // AuthCallback handles the session exchange and would race with this check)
  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const login = (provider = 'google') => {
    const redirectUrl = window.location.origin + '/dashboard';
    if (provider === 'discord') {
      // Discord OAuth - redirect to backend which handles the OAuth flow
      window.location.href = `${API}/auth/discord/login?redirect=${encodeURIComponent(redirectUrl)}`;
    } else {
      // Google OAuth via Emergent
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }
    setUser(null);
    setIsAuthenticated(false);
  };

  const processSessionId = async (sessionId) => {
    try {
      const response = await fetch(`${API}/auth/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId })
      });
      
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        setIsAuthenticated(true);
        return data.user;
      }
      return null;
    } catch (error) {
      console.error('Session processing failed:', error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Update user state directly (for immediate UI update after login)
  const updateUser = useCallback((userData) => {
    if (userData) {
      setUser(userData);
      setIsAuthenticated(true);
    }
  }, []);

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    checkAuth,
    processSessionId,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// AuthCallback component - handles the session_id from URL (Google Auth)
export const AuthCallback = () => {
  const { processSessionId, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        const user = await processSessionId(sessionId);
        
        if (user) {
          // Clear the hash and redirect to dashboard - state is already updated
          window.history.replaceState(null, '', '/');
          navigate('/', { replace: true });
        } else {
          navigate('/login', { replace: true });
        }
      } else {
        // Check if coming from Discord OAuth (has session cookie set)
        const user = await checkAuth();
        if (user) {
          navigate('/', { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      }
    };

    processAuth();
  }, [processSessionId, checkAuth, navigate, location.hash]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground font-mono">Authenticating...</p>
      </div>
    </div>
  );
};

// Discord OAuth Callback component
export const DiscordCallback = () => {
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const handleCallback = async () => {
      // After Discord OAuth, session cookie is already set by backend
      // Just need to refresh auth state
      await checkAuth();
      navigate('/', { replace: true });
    };

    handleCallback();
  }, [checkAuth, navigate]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground font-mono">Completing Discord login...</p>
      </div>
    </div>
  );
};

export default AuthContext;
